import { Avatar, Combobox, Group, Loader, Text, TextInput, useCombobox } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraFieldData2, JiraUserLiteData } from '../../../../api/endpoints/jira';
import { useResolve } from '../../../Container';

interface OptionInputProps {
    field: JiraFieldData2;
    value: JiraUserLiteData | JiraUserLiteData[] | null;
    error?: boolean;
    onChange: (value: JiraUserLiteData | JiraUserLiteData[] | null) => void;
    onSubmit: (value: JiraUserLiteData | JiraUserLiteData[] | null) => void;
}

const useJiraUserQuery = (jiraProjectId: string, query: string) => {
    const api = useResolve(ApiClient);

    const [debouncedQuery] = useDebounce(query, 250);
    return useQuery({
        queryKey: ['jira.projects.users.list', jiraProjectId, debouncedQuery],
        queryFn() {
            return api.exec(v2.jira.projects.users.list(jiraProjectId, debouncedQuery || ''));
        },
    });
};

export const JiraUserInput = React.memo(({ field, value, error, onChange, onSubmit }: OptionInputProps) => {

    // So, single user custom field values need to be an array of one (shrug).
    const arrayMode = field.name.includes('customfield');
    const realValue = (Array.isArray(value) ? value?.[0] : value) || null;
    const fromRealValue = useCallback((v: JiraUserLiteData) => arrayMode ? [v] : v, [arrayMode]);

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState('');
    const { data, isLoading } = useJiraUserQuery(field.project.id, search);
    const users = data || [];
    const empty = search.length > 0 && users.length === 0 && !isLoading;

    const handleSearch = (query: string) => {
        setSearch(query);
    };

    // Find user by ID
    const findUserById = (id: string): JiraUserLiteData | null => {
        if (!id || !users.length) return null;
        return users.find(user => user.id === id) || null;
    };

    return (
        <Combobox
            onOptionSubmit={(optionValue) => {
                const user = findUserById(optionValue);
                onChange(fromRealValue(user));
                onSubmit(fromRealValue(user));
                combobox.closeDropdown();
            }}
            withinPortal={false}
            store={combobox}
        >
            <Combobox.Target>
                <TextInput
                    label={field.name}
                    placeholder={`Search for ${field.name.toLowerCase()}`}
                    required={field.required && !field.hasDefaultValue}
                    value={realValue ? realValue.displayName : search}
                    error={error}
                    onChange={(event) => {
                        const query = event.currentTarget.value;
                        if (realValue) {
                            // Clear the selected value if user starts typing
                            onChange(null);
                        }
                        setSearch(query);
                        handleSearch(query);
                        combobox.resetSelectedOption();
                        combobox.openDropdown();
                    }}
                    onClick={() => {
                        combobox.openDropdown();
                        if (search.length > 0 && !data) {
                            handleSearch(search);
                        }
                    }}
                    onFocus={() => {
                        combobox.openDropdown();
                        if (search.length > 0 && !data) {
                            handleSearch(search);
                        }
                    }}
                    onBlur={() => combobox.closeDropdown()}
                    rightSection={isLoading ? <Loader size={18} /> : null}
                />
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {users.map((user) => (
                        <Combobox.Option value={user.id} key={user.id}>
                            <Group gap="sm" align="center">
                                {user.avatarUrl && (
                                    <Avatar size="md" src={user.avatarUrl} alt={user.displayName} />
                                )}
                                <Text>{user.displayName}</Text>
                            </Group>
                        </Combobox.Option>
                    ))}
                    {empty && <Combobox.Empty>No users found</Combobox.Empty>}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
});
